import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';

import { BaseMapComponent } from '@app/app/base/components/base-map.component';
import { RoutingService } from '@app/app/common/routing.service';
import { TagManagerService } from '@app/app/common/tag.service';
import { GeocodedArea } from '@app/app/gis/model/geocodedArea';
import { PoiListItem } from '@app/app/gis/model/poibase';
import { GisService } from '@app/app/gis/services/gis.service';
import { NeedsCacheService } from '@app/app/gis/services/needs-cache.service';
import { fromDateToNgbDateStruct } from '@app/app/gis/util/dateutil';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigurationService } from 'src/app/config/configuration.service';
import { ControllerService } from 'src/app/controller.service';
import { BaseLocationService } from 'src/app/gis/location/base.location.service';
import 'src/app/gis/model/spiderfier';
import { MapService } from 'src/app/gis/services/map.service';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent extends BaseMapComponent implements OnDestroy, AfterViewInit {
  @ViewChild('mapWrapper') mapWrapper: ElementRef;

  public needId: number;

  constructor(
    // NOSONAR
    configurationService: ConfigurationService,
    controllerService: ControllerService,
    environmentInjector: EnvironmentInjector,
    injector: Injector,
    mapService: MapService,
    locationService: BaseLocationService,
    needsService: NeedsCacheService,
    logger: NGXLogger,
    @Inject(DOCUMENT) document: Document,
    protected routingService: RoutingService,
    protected gisService: GisService,
    protected readonly tagManagerService: TagManagerService,
    @Inject(PLATFORM_ID) platformId: any
  ) {
    // eslint-disable-next-line max-len
    super(
      configurationService,
      controllerService,
      injector,
      environmentInjector,
      mapService,
      locationService,
      needsService,
      logger,
      document,
      platformId
    );

    if (window && 'IntersectionObserver' in window) {
      this.mapObserver = new IntersectionObserver(
        ([entry]: IntersectionObserverEntry[]) => {
          if (!this.mapWrapper) {
            return;
          }
          this.mapWrapper.nativeElement.classList.toggle('map-mostly-visible', entry.intersectionRatio >= 0.8);
        },
        {
          threshold: 0.8,
        }
      );
    }
  }

  @HostListener('window:scroll') onScroll() {
    if (!this.mapWrapper.nativeElement) {
      return;
    }

    const isSticky = this.mapWrapper.nativeElement.getBoundingClientRect().top <= 0;
    this.mapWrapper.nativeElement.classList.toggle('is-sticky', isSticky);
  }

  protected markerClicked(item: PoiListItem) {
    if (item && item.id) {
      /* eslint-disable @typescript-eslint/naming-convention */
      this.tagManagerService.click({
        event: 'select_content',
        type: 'places_poi',
        label: item.serviceType.id,
        text: item.name.toLowerCase(),
        additional_info: 'map',
      });
      /* eslint-enable @typescript-eslint/naming-convention */
      this.controllerService.showPoi(item);
    }
  }

  protected async getSecondLevelNeedId(): Promise<string> {
    const needId = this.routingService.getNeedIdParam();

    if (this.needs.length > 0) {
      const selectedNeed = this.needs.find((x) => x.id === Number(needId));
      const selectedProductNeed = this.needs.find((n) => n.secondlevelneeds.some((s) => s.id === needId));

      if (selectedNeed) {
        return selectedNeed.secondlevelneeds[0].id;
      } else if (selectedProductNeed) {
        return needId;
      } else {
        if (needId) {
          this.logger.info(`NeedID not found: ${needId}`);
        }
        const defaultId = await firstValueFrom(this.needsService.getDefaultSecondLevelNeedId());
        return defaultId;
      }
    }

    // fallback if this.needs is empty
    const fallbackId = await firstValueFrom(this.needsService.getDefaultSecondLevelNeedId());
    return fallbackId;
  }

  protected async startFirstSearch() {
    const secondLevelNeedId = await this.getSecondLevelNeedId();

    const need = this.needs.filter((n) => n.secondlevelneeds.some((s) => s.id === secondLevelNeedId));
    const searchParameters = this.controllerService.getDefaultSearchParams(need[0].id, secondLevelNeedId);

    const time = this.routingService.getQueryParam('time');
    if (time) {
      let date;
      if (time.toLowerCase() === 'now') {
        date = new Date();
      } else if (time.toLowerCase() === 'today16') {
        date = new Date();
        date.setHours(16, 0, 0, 0);
      } else {
        date = new Date(time);
      }

      if (!isNaN(date)) {
        searchParameters.date = fromDateToNgbDateStruct(date);
        searchParameters.time = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        searchParameters.openNow = true;
      }
    }

    const locationQuery = this.routingService.getQueryParam('preselecttext');
    if (locationQuery) {
      this.gisService
        .geocodeByNeed(need[0].id, locationQuery)
        .pipe(map((items: GeocodedArea[]) => this.controllerService.sortGeocodedAreas(locationQuery, items)))
        .subscribe((geocodedAreas) => {
          if (geocodedAreas.length > 0) {
            searchParameters.location = geocodedAreas[0];
          } else {
            searchParameters.query = locationQuery;
          }

          this.controllerService.startSearch(searchParameters);
        });
    } else {
      this.controllerService.startSearch(searchParameters);
    }
  }
}
